/**
 * Rows of text → a LaTeX `tabular`, optionally inside a `table` float with a caption,
 * a label and `threeparttable` notes.
 *
 * Cell text is escaped by default: `& % $ # _ { }` get a backslash, and `~ ^ \` (plus
 * `< > |`, which the original LaTeX fonts print as other glyphs) become text commands.
 * Turn escaping off to write LaTeX in the cells yourself.
 */

export type ColumnAlign = "l" | "c" | "r";
export type RuleStyle = "booktabs" | "simple" | "grid" | "none";
export type Placement = "htbp" | "!htbp" | "h" | "t" | "b" | "p" | "H";
export type CaptionPosition = "above" | "below";

export interface LatexTableOptions {
  align: ColumnAlign[];
  rules: RuleStyle;
  /** The first row is a header: ruled off below, and bold if `boldHeader`. */
  headerRow: boolean;
  boldHeader: boolean;
  escape: boolean;
  /** Wrap the tabular in a `table` float (needed for a caption and a label). */
  float: boolean;
  placement: Placement;
  centering: boolean;
  caption: string;
  label: string;
  captionPosition: CaptionPosition;
  /** One note per entry, set under the table with `threeparttable`. */
  notes: string[];
  /** Begin the first note with an italic "Note." (APA style). */
  notePrefix: boolean;
}

export interface LatexTable {
  code: string;
  /** `\usepackage` lines the code needs, in preamble order. */
  packages: string[];
}

const LATEX_ESCAPES: Record<string, string> = {
  "\\": "\\textbackslash{}",
  "&": "\\&",
  "%": "\\%",
  $: "\\$",
  "#": "\\#",
  _: "\\_",
  "{": "\\{",
  "}": "\\}",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
  "<": "\\textless{}",
  ">": "\\textgreater{}",
  "|": "\\textbar{}",
};

const RE_LATEX_SPECIAL = /[\\&%$#_{}~^<>|]/g;
const RE_LINE_BREAKS = /\s*\r?\n\s*/g;
const RE_NUMBER = /^[-+−]?(?:\d[\d,]*(?:\.\d+)?|\.\d+)%?$/;
const RE_LABEL_UNSAFE = /[^A-Za-z0-9:._-]+/g;
const RE_TAB = /\t/;
const RE_SEMICOLON = /;/g;
const RE_COMMA = /,/g;
const RE_BLANK_ROW = /^\s*$/;

/** Text safe to place in a LaTeX document in text mode. */
export function escapeLatex(text: string): string {
  return text.replace(RE_LATEX_SPECIAL, (ch) => LATEX_ESCAPES[ch] ?? ch);
}

/** A cell's content: line breaks inside a cell become spaces, then escaping. */
function cellText(text: string, escapeSpecial: boolean): string {
  const flat = text.replace(RE_LINE_BREAKS, " ").trim();
  return escapeSpecial ? escapeLatex(flat) : flat;
}

function countMatches(text: string, pattern: RegExp): number {
  return (text.match(pattern) ?? []).length;
}

/** Tab if the first line has one; semicolon if it has more of those than commas. */
export function detectDelimiter(text: string): "\t" | ";" | "," {
  const firstLine = text.split("\n", 1)[0] ?? "";
  if (RE_TAB.test(firstLine)) {
    return "\t";
  }
  return countMatches(firstLine, RE_SEMICOLON) >
    countMatches(firstLine, RE_COMMA)
    ? ";"
    : ",";
}

/** Reads delimited text one character at a time (RFC 4180 quoting). */
class DelimitedReader {
  readonly rows: string[][] = [];
  row: string[] = [];
  field = "";
  quoted = false;
  readonly delimiter: string;

  constructor(delimiter: string) {
    this.delimiter = delimiter;
  }

  endField(): void {
    this.row.push(this.field);
    this.field = "";
  }

  endRow(): void {
    this.endField();
    const blank = this.row.length === 1 && RE_BLANK_ROW.test(this.row[0]);
    if (!blank) {
      this.rows.push(this.row.map((cell) => cell.trim()));
    }
    this.row = [];
  }

  /** Inside quotes: `""` is a quote, `"` ends the field's quoted part. Returns chars used. */
  quotedChar(ch: string, next: string | undefined): number {
    if (ch === '"' && next === '"') {
      this.field += '"';
      return 2;
    }
    if (ch === '"') {
      this.quoted = false;
    } else {
      this.field += ch;
    }
    return 1;
  }

  plainChar(ch: string, next: string | undefined): number {
    if (ch === '"' && this.field.trim() === "") {
      this.quoted = true;
      this.field = "";
    } else if (ch === this.delimiter) {
      this.endField();
    } else if (ch === "\n" || ch === "\r") {
      this.endRow();
      return ch === "\r" && next === "\n" ? 2 : 1;
    } else {
      this.field += ch;
    }
    return 1;
  }
}

/**
 * Parse CSV or TSV: quoted fields may hold delimiters, line breaks and doubled quotes
 * (`""`), as in RFC 4180. Blank lines are skipped.
 */
export function parseDelimited(text: string): string[][] {
  const reader = new DelimitedReader(detectDelimiter(text));
  let index = 0;
  while (index < text.length) {
    const ch = text[index];
    const next = text[index + 1];
    index += reader.quoted
      ? reader.quotedChar(ch, next)
      : reader.plainChar(ch, next);
  }
  if (reader.field !== "" || reader.row.length > 0) {
    reader.endRow();
  }
  return reader.rows;
}

/** Every row padded to the widest one. */
export function normalizeRows(rows: string[][]): string[][] {
  const width = Math.max(0, ...rows.map((row) => row.length));
  return rows.map((row) =>
    Array.from({ length: width }, (_, index) => row[index] ?? "")
  );
}

/** Columns whose body cells are all numbers (blanks allowed) — right-aligned by default. */
export function suggestAlignment(
  rows: string[][],
  headerRow: boolean
): ColumnAlign[] {
  const table = normalizeRows(rows);
  const body = headerRow ? table.slice(1) : table;
  const width = table[0]?.length ?? 0;
  return Array.from({ length: width }, (_, column) => {
    const cells = body
      .map((row) => row[column].trim())
      .filter((cell) => cell !== "");
    return cells.length > 0 && cells.every((cell) => RE_NUMBER.test(cell))
      ? "r"
      : "l";
  });
}

export function sanitizeLabel(label: string): string {
  return label.trim().replace(RE_LABEL_UNSAFE, "-");
}

function columnSpec(align: ColumnAlign[], rules: RuleStyle): string {
  return rules === "grid" ? `|${align.join("|")}|` : align.join("");
}

/** The rule above the table, below the header, and at the bottom, for each style. */
const RULES: Record<RuleStyle, { top: string; mid: string; bottom: string }> = {
  booktabs: { top: "\\toprule", mid: "\\midrule", bottom: "\\bottomrule" },
  simple: { top: "\\hline", mid: "\\hline", bottom: "\\hline" },
  grid: { top: "\\hline", mid: "\\hline", bottom: "\\hline" },
  none: { top: "", mid: "", bottom: "" },
};

function tabularLines(rows: string[][], options: LatexTableOptions): string[] {
  const table = normalizeRows(rows);
  const width = table[0]?.length ?? 0;
  const align = Array.from(
    { length: width },
    (_, index) => options.align[index] ?? "l"
  );
  const rules = RULES[options.rules];
  const lines = [`\\begin{tabular}{${columnSpec(align, options.rules)}}`];
  const body: string[] = [];
  if (rules.top) {
    body.push(rules.top);
  }
  for (const [index, row] of table.entries()) {
    const header = options.headerRow && index === 0;
    const cells = row.map((cell) => {
      const text = cellText(cell, options.escape);
      return header && options.boldHeader && text ? `\\textbf{${text}}` : text;
    });
    body.push(`${cells.join(" & ")} \\\\`);
    const last = index === table.length - 1;
    if (header && rules.mid && !last) {
      body.push(rules.mid);
    } else if (options.rules === "grid" && !last) {
      body.push("\\hline");
    }
  }
  if (rules.bottom) {
    body.push(rules.bottom);
  }
  lines.push(...body.map((line) => `  ${line}`));
  lines.push("\\end{tabular}");
  return lines;
}

function noteLines(options: LatexTableOptions): string[] {
  const notes = options.notes.map((note) => note.trim()).filter(Boolean);
  if (notes.length === 0) {
    return [];
  }
  const items = notes.map((note, index) => {
    const text = cellText(note, options.escape);
    const prefix = options.notePrefix && index === 0 ? "\\textit{Note.} " : "";
    return `  \\item ${prefix}${text}`;
  });
  return ["\\begin{tablenotes}", "  \\small", ...items, "\\end{tablenotes}"];
}

function indent(lines: string[], depth = 1): string[] {
  const pad = "  ".repeat(depth);
  return lines.map((line) => `${pad}${line}`);
}

/** The table's middle: caption, label, tabular and notes in reading order. */
function captionedBody(rows: string[][], options: LatexTableOptions): string[] {
  const caption: string[] = [];
  if (options.float && options.caption.trim()) {
    caption.push(`\\caption{${cellText(options.caption, options.escape)}}`);
  }
  const label = sanitizeLabel(options.label);
  if (options.float && label) {
    caption.push(`\\label{${label}}`);
  }
  const tabular = tabularLines(rows, options);
  const notes = noteLines(options);
  const main =
    options.captionPosition === "above"
      ? [...caption, ...tabular, ...notes]
      : [...tabular, ...notes, ...caption];
  if (notes.length === 0) {
    return main;
  }
  return ["\\begin{threeparttable}", ...indent(main), "\\end{threeparttable}"];
}

export function buildLatexTable(
  rows: string[][],
  options: LatexTableOptions
): LatexTable {
  const packages: string[] = [];
  if (options.rules === "booktabs") {
    packages.push("\\usepackage{booktabs}");
  }
  if (options.notes.some((note) => note.trim())) {
    packages.push("\\usepackage{threeparttable}");
  }
  if (options.float && options.placement === "H") {
    packages.push("\\usepackage{float}");
  }
  const body = captionedBody(rows, options);
  if (!options.float) {
    return { code: `${body.join("\n")}\n`, packages };
  }
  const lines = [
    `\\begin{table}[${options.placement}]`,
    ...(options.centering ? ["  \\centering"] : []),
    ...indent(body),
    "\\end{table}",
  ];
  return { code: `${lines.join("\n")}\n`, packages };
}
